/**
 * lib/purchaseOrders.ts
 * ------------------------------------------------------------------
 * PURCHASE ORDERS, GOODS RECEIVED, AND THE THREE-WAY MATCH.
 *
 * The requisition chain already answers "may we buy this" — requester, HOD,
 * principal. It stops there. What it never produced is the document that makes
 * the school's side of the deal explicit and checkable:
 *
 *   ORDERED   what we told the supplier to send, at what price   (this file)
 *   RECEIVED  what actually arrived, counted by whoever took it  (this file)
 *   INVOICED  what the supplier is asking to be paid            (the voucher)
 *
 * Those three agreeing is the single most effective control in procurement, and
 * its absence is how schools pay for goods that never came, or pay a price
 * nobody agreed. Approving an invoice is not a substitute: the approver sees the
 * supplier's own figures and has nothing to compare them against.
 *
 * WHAT MATCHING ACTUALLY CATCHES, and why each is separate below:
 *   · over-delivery   — more arrived than was ordered; someone must accept the
 *                       extra cost before it is paid, not after
 *   · short delivery  — paying the full invoice for part of the order
 *   · price creep     — the agreed unit price quietly rising between order and
 *                       invoice, which a value-only check hides inside a total
 *   · duplicate bill  — a second invoice against an order already fully invoiced
 *
 * A variance is never silently absorbed. `matchPurchaseOrder` reports it, and
 * the caller decides whether it may be accepted — a tolerance is a policy, not
 * a fact, so it is an input here rather than a constant.
 *
 * PURE. Types, numbering, and the match. No Firestore, no React.
 * ------------------------------------------------------------------ */

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

export const PO_COLLECTIONS = {
  orders: 'financePurchaseOrders',
  receipts: 'financeGoodsReceipts',
  counters: 'financeCounters',
} as const;

export const poCounterId = (year: string): string => `purchaseOrders-${year}`;
export const grnCounterId = (year: string): string => `goodsReceived-${year}`;
export const formatPoNo = (seq: number, year: string): string =>
  `PO/${year}/${String(seq).padStart(4, '0')}`;
export const formatGrnNo = (seq: number, year: string): string =>
  `GRN/${year}/${String(seq).padStart(4, '0')}`;

/* =============================== types =============================== */

/**
 * `issued` is the meaningful state: before it, the order is ours to change;
 * after it, the supplier has been told, and changing quantities or prices means
 * amending an order the school has already committed to. That is why receiving
 * and matching only apply from `issued` onwards.
 *
 * There is no `paid` — payment is the voucher's state, and duplicating it here
 * would create two answers to "has this been paid".
 */
export type PoStatus =
  | 'draft'
  | 'issued'
  | 'part_received'
  | 'received'
  | 'closed'
  | 'cancelled';

export const PO_STATUS_LABEL: Record<PoStatus, string> = {
  draft: 'Draft',
  issued: 'Issued to supplier',
  part_received: 'Partly received',
  received: 'Received in full',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export interface PoLine {
  description: string;
  qty: number;
  unitCost: number;
  lineTotal: number;
  /** GL account this line will be coded to. Captured at ORDER time, because the
   *  person who knows what a thing is for is the one ordering it. */
  accountCode?: string;
  unit?: string;
}

export interface PurchaseOrder {
  id: string;
  poNo: string;
  /** The requisition that authorised it, when there was one. A PO without a
   *  requisition is legitimate (a recurring utility, an emergency) but should be
   *  visibly so. */
  requisitionId?: string;
  reqNo?: string;
  supplierId?: string;
  supplierName: string;
  lines: PoLine[];
  total: number;
  fy: string;
  status: PoStatus;
  /** When the order was placed with the supplier — not when it was drafted. */
  issuedAt?: string;
  expectedDate?: string;
  notes?: string;
  /** Vouchers raised against this order. A list, because part-deliveries are
   *  legitimately invoiced separately. */
  voucherIds?: string[];
  date: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt?: string;
  cancelledReason?: string;
}

export interface GrnLine {
  /** Index into the order's `lines`. Positional rather than by description,
   *  because two lines can legitimately read the same. */
  poLineIdx: number;
  qtyReceived: number;
  note?: string;
}

export interface GoodsReceipt {
  id: string;
  grnNo: string;
  poId: string;
  poNo: string;
  supplierName: string;
  lines: GrnLine[];
  /** Date the goods physically arrived, which is not the date this was keyed. */
  receivedDate: string;
  receivedBy: string;
  receivedByName?: string;
  /** Delivery note / waybill number from the supplier. */
  deliveryRef?: string;
  notes?: string;
  createdAt: string;
}

/* ============================== the match ============================== */

export interface LineMatch {
  idx: number;
  description: string;
  unitCost: number;
  qtyOrdered: number;
  qtyReceived: number;
  qtyOutstanding: number;
  /** Ordered value for the quantity actually received — what we should pay. */
  valueReceived: number;
  overDelivered: boolean;
  shortDelivered: boolean;
}

export type MatchVerdict =
  | 'not-received'
  | 'part-received'
  | 'ready-to-pay'
  | 'over-delivered'
  | 'over-invoiced'
  | 'under-invoiced'
  | 'settled';

export const MATCH_VERDICT_LABEL: Record<MatchVerdict, string> = {
  'not-received': 'Nothing received yet',
  'part-received': 'Partly received',
  'ready-to-pay': 'Matched — clear to pay',
  'over-delivered': 'More received than ordered',
  'over-invoiced': 'Invoiced above what was received',
  'under-invoiced': 'Invoiced below what was received',
  settled: 'Received and invoiced in full',
};

export interface ThreeWayMatch {
  lines: LineMatch[];
  ordered: number;
  /** Ordered value of what arrived. The figure the invoice is judged against —
   *  NOT the order total, which would pass a short delivery billed in full. */
  received: number;
  invoiced: number;
  /** invoiced − received. Positive means the supplier is asking for more than
   *  arrived. */
  variance: number;
  /** Whether the variance is inside the school's tolerance. */
  withinTolerance: boolean;
  verdict: MatchVerdict;
  /** Plain-language reasons, for the reviewer rather than the log. */
  issues: string[];
  fullyReceived: boolean;
}

/**
 * The school's default variance allowance, applied whichever is GREATER.
 *
 * P50 covers a delivery charge or a rounding difference on a small order; 1%
 * covers the same on a large one, where P50 would fire on every invoice. Set to
 * zero and every ninety-thebe rounding difference blocks a payment, which
 * teaches everyone to override the block — a control nobody trusts is worse
 * than none, because it makes the real variances look routine.
 */
export const DEFAULT_MATCH_TOLERANCE: MatchTolerance = { amount: 50, percent: 1 };

export interface MatchTolerance {
  /** Absolute pula either way. Covers rounding and small delivery charges. */
  amount?: number;
  /** Percentage of the received value, whichever is greater. */
  percent?: number;
}

/**
 * Compare ordered, received and invoiced.
 *
 * The invoice is judged against the value of what ARRIVED, not the order total.
 * That single choice is what makes the match worth running: judging against the
 * order would pass an invoice for the full amount when half the goods are still
 * on a truck somewhere, which is the most common way a school overpays.
 *
 * Quantities are compared per LINE and money in TOTAL, deliberately. A total-only
 * check nets a short delivery of one item against an over-delivery of another and
 * reports a clean match; a per-line money check would flag every legitimate
 * rounding difference. Each catches what the other cannot.
 */
export function matchPurchaseOrder(
  po: Pick<PurchaseOrder, 'lines' | 'total'>,
  receipts: GoodsReceipt[],
  invoicedTotal: number,
  tolerance: MatchTolerance = {},
): ThreeWayMatch {
  const receivedByLine = new Map<number, number>();
  receipts.forEach((g) => (g.lines ?? []).forEach((l) => {
    const cur = receivedByLine.get(l.poLineIdx) ?? 0;
    receivedByLine.set(l.poLineIdx, cur + (Number(l.qtyReceived) || 0));
  }));

  const lines: LineMatch[] = (po.lines ?? []).map((l, idx) => {
    const qtyOrdered = Number(l.qty) || 0;
    const qtyReceived = round2(receivedByLine.get(idx) ?? 0);
    const unitCost = Number(l.unitCost) || 0;
    return {
      idx,
      description: l.description,
      unitCost,
      qtyOrdered,
      qtyReceived,
      qtyOutstanding: round2(Math.max(0, qtyOrdered - qtyReceived)),
      valueReceived: round2(qtyReceived * unitCost),
      overDelivered: qtyReceived > qtyOrdered + 0.0001,
      shortDelivered: qtyReceived < qtyOrdered - 0.0001,
    };
  });

  const ordered = round2(po.total ?? lines.reduce((n, l) => n + l.qtyOrdered * l.unitCost, 0));
  const received = round2(lines.reduce((n, l) => n + l.valueReceived, 0));
  const invoiced = round2(invoicedTotal);
  const variance = round2(invoiced - received);

  const allowed = Math.max(
    tolerance.amount ?? 0,
    received * ((tolerance.percent ?? 0) / 100),
  );
  const withinTolerance = Math.abs(variance) <= allowed + 0.005;

  const issues: string[] = [];
  lines.filter((l) => l.overDelivered).forEach((l) => issues.push(
    `${l.description}: ${l.qtyReceived} received against ${l.qtyOrdered} ordered.`,
  ));
  lines.filter((l) => l.shortDelivered && l.qtyReceived > 0).forEach((l) => issues.push(
    `${l.description}: ${l.qtyOutstanding} still outstanding.`,
  ));
  if (invoiced > 0 && !withinTolerance) {
    issues.push(variance > 0
      ? `Invoiced ${variance.toFixed(2)} more than the value received.`
      : `Invoiced ${Math.abs(variance).toFixed(2)} less than the value received.`);
  }

  const anyReceived = lines.some((l) => l.qtyReceived > 0);
  const fullyReceived = lines.length > 0 && lines.every((l) => l.qtyReceived >= l.qtyOrdered - 0.0001);
  const anyOver = lines.some((l) => l.overDelivered);

  // Ordered by which fact a reviewer most needs to act on, not by severity of
  // the number. An over-delivery outranks everything because someone must accept
  // the extra cost. "Nothing arrived" outranks a money variance: an invoice
  // against an empty delivery is a delivery problem, and labelling it
  // over-invoiced would send the bursar to argue about price instead of asking
  // where the goods are. Both still block payment either way — the ordering
  // decides what the screen SAYS, and a misleading label is how a real problem
  // gets closed as a pricing query.
  let verdict: MatchVerdict;
  if (anyOver) verdict = 'over-delivered';
  else if (!anyReceived) verdict = 'not-received';
  else if (invoiced > 0 && !withinTolerance) verdict = variance > 0 ? 'over-invoiced' : 'under-invoiced';
  else if (!fullyReceived) verdict = 'part-received';
  else if (invoiced > 0) verdict = 'settled';
  else verdict = 'ready-to-pay';

  return {
    lines, ordered, received, invoiced, variance, withinTolerance,
    verdict, issues, fullyReceived,
  };
}

/** Verdicts that must not be paid without someone accepting the variance. */
export const BLOCKING_VERDICTS: MatchVerdict[] = [
  'not-received', 'over-delivered', 'over-invoiced',
];

export function isPayable(match: ThreeWayMatch): boolean {
  return !BLOCKING_VERDICTS.includes(match.verdict);
}

/** The status the order should now be in, derived from its receipts. Stored
 *  statuses drift; this is the one place that decides. */
export function poStatusFrom(
  current: PoStatus,
  match: ThreeWayMatch,
): PoStatus {
  if (current === 'draft' || current === 'cancelled' || current === 'closed') return current;
  if (match.fullyReceived) return 'received';
  if (match.lines.some((l) => l.qtyReceived > 0)) return 'part_received';
  return 'issued';
}

export function poLinesTotal(lines: PoLine[]): number {
  return round2(lines.reduce((n, l) => n + (Number(l.lineTotal) || 0), 0));
}

/** Keep `lineTotal` a derived figure, so a hand-edited total cannot disagree
 *  with qty × price — the discrepancy a supplier would happily invoice against. */
export function normalisePoLine(l: PoLine): PoLine {
  const qty = Number(l.qty) || 0;
  const unitCost = Number(l.unitCost) || 0;
  return { ...l, qty, unitCost, lineTotal: round2(qty * unitCost) };
}
